import { Controller } from "stimulus";

export default class extends Controller {
  connect() {
    console.log("Spider chart controller connected");

    // Get data from HTML data attributes (compatible with older Stimulus)
    this.sectionNames = JSON.parse(this.element.dataset.sectionNames || "[]");
    this.individualScores = JSON.parse(
      this.element.dataset.individualScores || "{}"
    );
    this.cohortScores = JSON.parse(this.element.dataset.cohortScores || "{}");
    this.sectionNamesMap = JSON.parse(
      this.element.dataset.sectionNamesMap || "{}"
    );

    this.initializeChart();
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  async initializeChart() {
    try {
      // Wait for Chart.js to be available (either from CDN or npm)
      await this.waitForChart();

      console.log("Chart.js loaded, creating spider chart");
      console.log("Section Names:", this.sectionNames);
      console.log("Individual Scores:", this.individualScores);
      console.log("Cohort Scores:", this.cohortScores);

      // Only create chart if we have data
      if (this.sectionNames.length > 0) {
        this.createChart();
      } else {
        console.log("No data available for chart");
        this.showNoDataMessage();
      }
    } catch (error) {
      console.error("Failed to initialize chart:", error);
      this.showErrorMessage();
    }
  }

  async waitForChart() {
    // Try to use Chart from global scope (CDN) or import it
    let Chart;

    if (window.Chart) {
      Chart = window.Chart;
      console.log("Using Chart.js from global scope (CDN)");
      return Chart;
    } else {
      try {
        // Try to import Chart.js (if installed via npm)
        const ChartModule = await import("chart.js");
        Chart = ChartModule.Chart || ChartModule.default || ChartModule;
        console.log("Using Chart.js from npm import");
        window.Chart = Chart; // Make it globally available
        return Chart;
      } catch (importError) {
        console.warn("Chart.js not found via import, trying CDN fallback");
        throw new Error("Chart.js not available");
      }
    }
  }

  createChart() {
    const ctx = this.element.querySelector("canvas").getContext("2d");

    // Create labels and data arrays
    const sectionLabels = this.sectionNames.map((num) => {
      const sectionName = this.sectionNamesMap[num] || `Section ${num}`;
      return `${num}. ${sectionName}`;
    });

    const individualData = this.sectionNames.map(
      (num) => this.individualScores[num] || 0
    );
    const cohortData = this.sectionNames.map(
      (num) => this.cohortScores[num] || 0
    );

    console.log("Chart Labels:", sectionLabels);
    console.log("Individual Data:", individualData);
    console.log("Cohort Data:", cohortData);

    this.chart = new Chart(ctx, {
      type: "radar",
      data: {
        labels: sectionLabels,
        datasets: [
          {
            label: "Your Performance",
            data: individualData,
            fill: true,
            backgroundColor: "rgba(74, 144, 226, 0.2)",
            borderColor: "#4a90e2",
            pointBackgroundColor: "#4a90e2",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: "#4a90e2",
            borderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
          {
            label: "Cohort Average",
            data: cohortData,
            fill: true,
            backgroundColor: "rgba(40, 167, 69, 0.2)",
            borderColor: "#28a745",
            pointBackgroundColor: "#28a745",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: "#28a745",
            borderWidth: 3,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 14,
                weight: "bold",
              },
            },
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "rgba(255, 255, 255, 0.1)",
            borderWidth: 1,
            callbacks: {
              label: function (context) {
                return `${context.dataset.label}: ${context.parsed.r.toFixed(
                  2
                )}`;
              },
            },
          },
        },
        scales: {
          r: {
            beginAtZero: true,
            max: 6,
            min: 0,
            ticks: {
              stepSize: 1,
              font: {
                size: 12,
              },
            },
            pointLabels: {
              font: {
                size: 12,
                weight: "bold",
              },
            },
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            angleLines: {
              color: "rgba(0, 0, 0, 0.1)",
            },
          },
        },
      },
    });

    console.log("Chart created successfully");
  }

  showNoDataMessage() {
    const canvas = this.element.querySelector("canvas");
    if (canvas) {
      canvas.style.display = "none";
    }

    const chartContainer = this.element.querySelector("[data-chart-container]");
    if (chartContainer) {
      chartContainer.innerHTML = `
        <div class="text-center p-5">
          <i class="fas fa-chart-line fa-3x text-muted mb-3"></i>
          <h5 class="text-muted">No Competency Data Available</h5>
          <p class="text-muted">
            Competency assessments need to be completed to display the spider chart.
          </p>
        </div>
      `;
    }
  }

  showErrorMessage() {
    const canvas = this.element.querySelector("canvas");
    if (canvas) {
      canvas.style.display = "none";
    }

    const chartContainer = this.element.querySelector("[data-chart-container]");
    if (chartContainer) {
      chartContainer.innerHTML = `
        <div class="text-center p-5">
          <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h5 class="text-muted">Chart Loading Error</h5>
          <p class="text-muted">
            Chart.js library could not be loaded. Please try refreshing the page.
          </p>
        </div>
      `;
    }
  }
}
